/**
 * API Interceptors
 * 
 * This file sets up request and response interceptors for API calls,
 * handling common tasks like authentication, error handling, and logging.
 */

import { AxiosInstance } from 'axios';
import { Modal } from 'antd';

declare module 'axios' {
  interface AxiosRequestConfig {
    handleErrorLocally?: boolean;
  }
}

/**
 * Helper function to detect content type from response
 */
const getContentType = (response: any) => {
  const contentType = response.headers['content-type'] || '';
  return contentType.toLowerCase();
};

/**
 * Setup request and response interceptors for the API client
 */
export function setupInterceptors(apiClient: AxiosInstance): void {
  // Request interceptor
  apiClient.interceptors.request.use(
    (config) => {
      // Add authorization header if token exists
      const token = localStorage.getItem('auth_token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      
      // Set responseType based on Accept header or specific URL patterns
      if (config.responseType !== 'blob' && config.url && (
        config.url.includes('/download') || 
        config.url.includes('/export') ||
        config.url.endsWith('pdf')
      )) {
        config.responseType = 'blob';
        // For PDF endpoints, we may want to set specific headers
        const acceptHeader = config.headers['Accept'];
        const isPdfIncluded = typeof acceptHeader === 'string' 
          ? acceptHeader.includes('application/pdf')
          : Array.isArray(acceptHeader) 
            ? acceptHeader.some(header => header.includes('application/pdf'))
            : false;
            
        if (!isPdfIncluded) {
          config.headers['Accept'] = 'application/pdf';
        }
      }
      
      return config;
    },
    (error) => {
      console.error('Request error:', error);
      return Promise.reject(error);
    }
  );

  // Response interceptor
  apiClient.interceptors.response.use(
    (response) => {
      // Check the content type of the response
      const contentType = getContentType(response);
      
      // Handle PDF responses
      if (contentType.includes('application/pdf')) {
        // For PDF responses, return the raw response
        return response;
      }
      
      // For JSON responses, apply our standard processing
      if (response.data && typeof response.data === 'object' && 'code' in response.data) {
        const { code, message, content } = response.data;
        
        // Define the success code list - easier to expand in the future
        const successCodes = ['A0000', 'P0000', 'M0000'];
        
        // If the code is not in the success codes list, show error and return modified response
        if (!successCodes.includes(code)) {
          if (response.config.handleErrorLocally) {
            return Promise.reject(Object.assign(new Error(message || '操作失敗'), {response}));
          }
          // Display error message using antd Modal
          Modal.error({
            title: '錯誤',
            content: message || '操作失敗'
          });
          
          // Log error to console for debugging
          console.error(`API Error: ${message} (Code: ${code})`);
          
          // Modify the response to maintain the AxiosResponse structure
          // but replace content with null
          response.data = { content: null };
          return response;
        }

        // For successful responses, modify the response to contain just the content
        response.data = { content };
        return response;
      }
      
      // Process other response types normally
      return response;
    },
    (error) => {
      let errorMessage = '系統發生錯誤';
      
      // Handle specific error cases
      if (error.response) {
        // Server responded with a status code outside the 2xx range
        const { status } = error.response;
        
        // Check if the error response has a message
        if (error.response.data && error.response.data.message) {
          errorMessage = error.response.data.message;
        } else {
          if (status === 401) {
            errorMessage = '未授權的訪問，請重新登入';
            // 清除本地存儲的身份驗證信息
            localStorage.removeItem('auth_token');
            localStorage.removeItem('login_time');
            // 重定向到登入頁面
            window.location.href = '/login';
          } else if (status === 403) {
            errorMessage = '無權限訪問此資源';
          } else if (status === 404) {
            errorMessage = '找不到資源';
          } else if (status >= 500) {
            errorMessage = '伺服器發生錯誤';
          }
        }
      } else if (error.request) {
        // Request was made but no response was received
        errorMessage = '網路連線失敗，請檢查您的網路連線';
      } else {
        // Error in setting up the request
        errorMessage = error.message || '請求發生錯誤';
      }
      
      // 由頁面負責提示的請求須保留原始錯誤，讓下載流程解析 JSON Blob。
      if (error.config?.handleErrorLocally) {
        return Promise.reject(error);
      }

      // Show error message using antd Modal
      Modal.error({
        title: '錯誤',
        content: errorMessage
      });
      
      // Log for debugging
      console.error('API Error:', error);
      
      // Return null instead of rejecting promise
      return Promise.resolve({ data: { content: null } });
    }
  );

  // Log initialization
  console.log('API interceptors initialized with PDF support');
}
